import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Check, Loader2 } from "lucide-react";

/**
 * BillingSuccessPage
 *
 * Two paths lead here:
 *
 * 1. **Stripe API Checkout (future path once prod key is restored):**
 *    Stripe redirects with `?session_id=cs_xxx`. We poll `/billing/status/:id`
 *    until the webhook marks it paid.
 *
 * 2. **Stripe Payment Link (current workaround):**
 *    Stripe redirects without a usable session_id (and no webhook fires since
 *    `STRIPE_WEBHOOK_SECRET` isn't set on prod). PricingPage stashed the
 *    plan the user clicked in `sessionStorage.ascend_last_checkout_plan`.
 *    We call `/billing/claim-payment-link` to optimistically grant it.
 */
export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking");
  const [planId, setPlanId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const claimPaymentLink = async (storedPlan) => {
      try {
        const r = await axios.post(`${API}/billing/claim-payment-link`, {
          plan_id: storedPlan,
        });
        if (cancelled) return;
        setStatus("paid");
        setPlanId(r.data.plan);
        sessionStorage.removeItem("ascend_last_checkout_plan");
        await checkAuth();
        setTimeout(() => navigate("/dashboard"), 1800);
      } catch (e) {
        if (cancelled) return;
        setStatus(e?.response?.status === 401 ? "needs_login" : "error");
      }
    };

    const run = async () => {
      const storedPlan = sessionStorage.getItem("ascend_last_checkout_plan");

      // No session_id AND no stashed plan → user hit this URL directly.
      if (!sessionId && !storedPlan) {
        navigate("/");
        return;
      }

      // Prefer the optimistic path when we have a stashed plan — Payment Links
      // never produce a session_id our backend can look up anyway.
      if (storedPlan) {
        await claimPaymentLink(storedPlan);
        return;
      }

      // Fallback: API Checkout path — poll until webhook confirms.
      let attempts = 0;
      const maxAttempts = 10;
      const poll = async () => {
        try {
          const r = await axios.get(`${API}/billing/status/${sessionId}`);
          const ps = r.data.payment_status;
          if (ps === "paid") {
            if (cancelled) return;
            setStatus("paid");
            setPlanId(r.data.plan_id);
            await checkAuth();
            setTimeout(() => navigate("/dashboard"), 1800);
            return;
          }
          if (r.data.status === "expired") {
            if (!cancelled) setStatus("expired");
            return;
          }
          if (attempts++ < maxAttempts && !cancelled) {
            setTimeout(poll, 2000);
          } else if (!cancelled) {
            setStatus("timeout");
          }
        } catch (e) {
          if (!cancelled) setStatus("error");
        }
      };
      poll();
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-6">
      <div className="max-w-md text-center" data-testid="billing-success-page">
        {status === "checking" && (
          <>
            <Loader2 size={36} className="mx-auto mb-6 text-[#486a8f] animate-spin" />
            <p className="font-mono-eyebrow text-[#486a8f] mb-3">Confirming…</p>
            <h1 className="font-display text-[28px] text-[#0f1724] tracking-tight">
              Finalizing your subscription…
            </h1>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#3e6b52] flex items-center justify-center">
              <Check size={28} className="text-white" strokeWidth={3} />
            </div>
            <p className="font-mono-eyebrow text-[#3e6b52] mb-3">You're in</p>
            <h1 className="font-display text-[34px] text-[#0f1724] tracking-tight leading-tight mb-3">
              Welcome to Ascend{planId && planId.startsWith("advanced") ? " Advanced" : ""}.
            </h1>
            <p className="font-body text-[15px] text-[#4b5563] leading-relaxed mb-2">
              Secure checkout complete. Your access is active.
            </p>
            <p className="font-body text-[12px] text-[#6b7280] leading-relaxed mb-1">
              You're paying for personal growth tools, AI-guided structure, and accountability support — not specific outcomes. Cancel anytime.
            </p>
            <p className="font-body text-[13px] text-[#4b5563] mt-4">Taking you to your dashboard…</p>
          </>
        )}
        {status === "needs_login" && (
          <>
            <p className="font-mono-eyebrow text-[#b94e4e] mb-3">One more step</p>
            <h1 className="font-display text-[28px] text-[#0f1724] tracking-tight leading-tight mb-3">
              Log in to activate your plan.
            </h1>
            <p className="font-body text-[#4b5563] mb-6">
              Your Stripe payment went through. Log in with the same email you used at checkout to unlock access.
            </p>
            <button
              data-testid="billing-success-login-btn"
              onClick={() => navigate("/start")}
              className="px-6 py-3 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[14px]"
            >
              Log in
            </button>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <p className="font-mono-eyebrow text-[#b94e4e] mb-3">Hmm</p>
            <h1 className="font-display text-[28px] text-[#0f1724] tracking-tight leading-tight mb-3">
              We couldn't confirm your payment.
            </h1>
            <p className="font-body text-[#4b5563] mb-6">
              If you were charged, give it a minute and refresh. Otherwise try again, or email support with your receipt.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                data-testid="billing-success-retry-btn"
                onClick={() => navigate("/pricing")}
                className="px-6 py-3 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[14px]"
              >
                Back to plans
              </button>
              <button
                data-testid="billing-success-support-btn"
                onClick={() => navigate("/support")}
                className="px-6 py-3 rounded-full bg-white border border-[#eadfc9] text-[#2b3442] font-medium text-[14px] hover:bg-[#faf6ec]"
              >
                Contact support
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
